import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiGqlClient } from "~/libs/gql";
import { graphql } from "~/types/__generated/gql";
import {
	CreateProcessMutation,
	CreateProcessMutationVariables,
} from "~/types/__generated/gql/graphql";
import {
	getProcessesQueryKey,
	GetProcessesResults,
	sortProcessesAndFiles,
} from "../useGetProcesses";

const query = /* GraphQL */ `
	mutation CreateProcess($input: CreateProcessInput!) {
		createProcess(input: $input) {
			_id
			createdAt
			updatedAt
			leadTime
			proccessRemovalDuration
			name
			sortingIndex
			_files {
				_id
				createdAt
				updatedAt
				customFields {
					customField
					textValue
					selectValues
					dateTimeValue

					_customField {
						name
						type
					}
				}
				processPaths {
					process
					leadTime
					proccessRemovalDuration
				}
				processLastUpdatedAt
				isProcessed
				products {
					_id
					createdAt
					updatedAt
					originalPrice
					originalPriceAddons {
						vat {
							percentage
							amount
						}
					}
					listedPrice
					unitPrice
					quantity
					subtotal
					subtotalAddons {
						additionalFee {
							isDivided
							type
							value
							amount
							additionalFee
						}
						fixDiscount {
							isDivided
							type
							value
							amount
							fixDiscount
						}
						discount {
							isDivided
							type
							value
							amount
						}
						vat {
							isIncluded
							percentage
							amount
						}
						adjustment {
							amount
						}
					}
					noVat
					netAmount
					grossAmount
					cancelledAmount
					category
					type
					product
					options {
						_id
						createdAt
						updatedAt
						originalPrice
						originalPriceAddons {
							vat {
								percentage
								amount
							}
						}
						listedPrice
						quantity
						subtotal
						subtotalAddons {
							additionalFee {
								isDivided
								type
								value
								amount
								additionalFee
							}
							fixDiscount {
								isDivided
								type
								value
								amount
								fixDiscount
							}
							discount {
								isDivided
								type
								value
								amount
							}
							vat {
								isIncluded
								percentage
								amount
							}
							adjustment {
								amount
							}
						}
						netAmount
						grossAmount
						cancelledAmount
						option

						_option {
							_id
							name
						}
					}

					_product {
						_id
						name
					}
				}
				sortingIndex
				counterConfigs {
					file {
						counter
						index

						_counter {
							_id
							createdAt
							updatedAt
							prefix
							suffix_digit
							isPredefinedFormat
						}
					}
					invoice {
						counter
						index

						_counter {
							_id
							createdAt
							updatedAt
							prefix
							suffix_digit
							isPredefinedFormat
						}
					}
					monthlyStatement {
						counter
						index

						_counter {
							_id
							createdAt
							updatedAt
							prefix
							suffix_digit
							isPredefinedFormat
						}
					}
				}
				fileSuffixComment
				receiptSuffixComment
				receipt
				issueDate
				dueDate

				_process {
					_id
					createdAt
					updatedAt
					name
				}
				_user {
					_id
					createdAt
					updatedAt
					firstName
					lastName
					avatar
				}
				_client {
					_id
					createdAt
					updatedAt
					name
				}
				_agent {
					_id
					createdAt
					updatedAt
					name
				}
				_assignees {
					_id
					createdAt
					updatedAt
					firstName
					lastName
					avatar
				}
			}
		}
	}
`;

export type CreateProcessVariables = CreateProcessMutationVariables;
export type CreateProcessResults = CreateProcessMutation["createProcess"];

export const useCreateProcess = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (variables: CreateProcessVariables) =>
			apiGqlClient
				.request(graphql(query), variables)
				.then((res) => res.createProcess),
		onSuccess: (data) => {
			queryClient.setQueryData<GetProcessesResults>(
				getProcessesQueryKey(),
				(oldData) =>
					oldData ? sortProcessesAndFiles([...oldData, data]) : [data],
			);
		},
	});
};
